import React, { useContext, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Slider from '@material-ui/core/Slider';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import FormatSizeIcon from '@material-ui/icons/FormatSize';
import SketchPreviewButton from './SketchPreviewButton';
import GenerateButton from './GenerateButton';
import { SketchToolContext } from './context';
import {
  updateLayer,
  deleteLayer,
  fetchExistingSketchSuccess,
  saveSketchSuccess,
  fetchLayerData,
  fetchLayerDataSuccess,
  fetchLayerDataFailure,
  updateSketch,
  updateZoom,
  updateMeasurementFontSize,
  updateCenter,
  deleteLayers,
  setSelectedLayerId,
  setSelectedPopupLayerId,
  setMode,
  fetchExistingTapeSketchSuccess,
  saveTapeSketchSuccess,
  fetchTapeSketchConfigSuccess,
  replaceTapeFeatures,
} from './actions';
import LeafletMap, { MEASUREMENT_FONT_SIZE_MAX, MEASUREMENT_FONT_SIZE_MIN } from '../LeafletMap';
import LayerMenu from './LayerMenu';
import LayerDialog from './LayerDialog';
import TapeFeaturePanel, { ConnectedTapeCanvas, ConnectedAerialCanvas } from './TapeTool';
import TapeSketchSheet from './TapeTool/TapeSketchSheet';
import { getFeatureTypeCode } from './TapeTool/tapeUtils';
import { convertGeoSketchToTapeFeatures } from './TapeTool/legacyConvert';
import {
  getSketches,
  saveSketch as saveSketchApi,
  fetchLayerData as fetchLayerDataApi,
  getTapeSketch,
  fetchTapeSketchConfig,
  saveTapeSketch as saveTapeSketchApi,
  uploadTapeSketch as uploadTapeSketchApi,
  completeTapeSketch as completeTapeSketchApi,
} from '../../services/orderReview.service';
import { getLabelStartingPosition, snapLatLngs } from './utils';
import { Typography, CircularProgress, TextField } from '@material-ui/core';
import html2canvas from 'html2canvas';
import { withStyles } from '@material-ui/styles';

const CANVAS_ID = 'sketchPreviewCanvas';
const TAPE_CANVAS_ID = 'tapeSketchPreviewCanvas';

const blobToBase64 = blob =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const styles = theme => ({});

const SketchTool = withStyles(styles)(({ classes, onSave, orderLineId, orderFormHeaderId, policyHolderLat, policyHolderLng }) => {
  let mapRef = useRef(null);
  const {
    state: {
      layers,
      center,
      geoJson,
      sketch,
      loading,
      error,
      zoomLevel,
      measurementFontSize,
      selectedLayerId,
      selectedPopupLayerId,
      mode,
      tapeFeatures,
      tapeConfig,
      tapeSketch,
    },
    dispatch,
  } = useContext(SketchToolContext);
  const [leafletData, setLeafletData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generatingCanvas, setGeneratingCanvas] = useState(false);
  const [base64, setBase64] = useState(false);
  const [showLayerHasLabelError, setShowLayerHasLabelError] = useState(false);
  const [tapeSaving, setTapeSaving] = useState(false);
  const [tapeGeneratingCanvas, setTapeGeneratingCanvas] = useState(false);
  const [tapeBase64, setTapeBase64] = useState(false);
  const tapeSketchSheetRef = useRef(null);

  /**
   * When the component is mounted: restore the map center from an existing sketch if one
   * exists (otherwise fall back to the order's policyholder address lat/long — already
   * loaded with the order, no separate geocoding lookup needed), load the Tape config and
   * any existing Tape sketch, and — if this order has an old GeoSketch but no Tape sketch
   * yet — auto-convert the old sketch into a Tape draft. The conversion only seeds
   * `tapeFeatures`; it flows through the same auto-save-as-draft (isComplete: false) path
   * as a hand-drawn sketch, so QA still has to review and explicitly complete it.
   */
  useEffect(() => {
    const fetchOldSketch = async () => {
      try {
        const existingSketch = await getSketches(orderFormHeaderId);
        if (existingSketch) {
          dispatch(fetchExistingSketchSuccess(existingSketch));
        }
        return existingSketch;
      } catch (e) {
        console.log('No existing sketch, centering on the policyholder address');
      }

      // No sketch yet — center on the order's policyholder address lat/long.
      if (policyHolderLat != null && policyHolderLng != null) {
        dispatch(updateCenter({ lat: Number(policyHolderLat), lng: Number(policyHolderLng) }));
      }
      return null;
    };
    const fetchLayerDataAsync = async () => {
      try {
        dispatch(fetchLayerData());
        const layerData = await fetchLayerDataApi();
        if (layerData) {
          dispatch(fetchLayerDataSuccess(layerData));
        } else {
          dispatch(fetchLayerDataFailure(new Error('Error fetching layer data')));
        }
        return layerData;
      } catch (e) {
        dispatch(fetchLayerDataFailure(e));
        return null;
      }
    };
    const fetchTapeSketchAsync = async () => {
      let tapeConfigResult = null;
      try {
        tapeConfigResult = await fetchTapeSketchConfig();
        dispatch(fetchTapeSketchConfigSuccess(tapeConfigResult));
      } catch (e) {
        console.log('Error fetching tape sketch config', e);
      }
      let existingTapeSketch = null;
      try {
        existingTapeSketch = await getTapeSketch(orderFormHeaderId);
        if (existingTapeSketch) {
          dispatch(fetchExistingTapeSketchSuccess(existingTapeSketch));
        }
      } catch (e) {
        console.log('Error fetching existing tape sketch', e);
      }
      return { tapeConfigResult, existingTapeSketch };
    };

    const run = async () => {
      const [layerData, oldSketch, { tapeConfigResult, existingTapeSketch }] = await Promise.all([
        fetchLayerDataAsync(),
        fetchOldSketch(),
        fetchTapeSketchAsync(),
      ]);

      if (existingTapeSketch || !oldSketch || !layerData || !tapeConfigResult) {
        return;
      }
      try {
        const sketchJson = JSON.parse(oldSketch.sketchJson);
        const converted = convertGeoSketchToTapeFeatures(sketchJson, layerData, tapeConfigResult);
        if (converted.length > 0) {
          dispatch(replaceTapeFeatures(converted));
        }
      } catch (e) {
        console.log('Error converting legacy GeoSketch to Tape format', e);
      }
    };

    run();
  }, []);

  useEffect(() => {
    if (layers && layers.length > 0 && !saving) {
      setSaving(true);
      saveSketch();
    }
  }, [layers]);

  useEffect(() => {
    if (tapeFeatures && tapeFeatures.length > 0 && !tapeSaving) {
      setTapeSaving(true);
      saveTapeSketch();
    }
    // Also re-save when the "Move All" tool shifts the sketch's origin anchor, or the
    // Aerial map is rotated — neither changes tapeFeatures itself (every feature's
    // position is derived from origin, and rotation is a separate saved preference).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tapeFeatures, tapeSketch?.originLat, tapeSketch?.originLng, tapeSketch?.bearing]);

  const saveSketch = async sketchBlobUrl => {
    const newSketchUri = sketchBlobUrl || (sketch && sketch.sketchUri ? sketch.sketchUri : 'http://localhost');

    try {
      const updatedSketch = {
        formAnswerSketchID: sketch && sketch.formAnswerSketchId ? sketch.formAnswerSketchId : undefined,
        orderFormHeaderId: orderFormHeaderId,
        sketchUri: newSketchUri,
        sketchJson: JSON.stringify({
          // Omit the leaflet Id and get the sqFt
          layers: layers.map(layer => {
            const sqFt = layer.sqFt;
            const { leafletId, layerOption, ...rest } = layer;
            return { ...rest, sqFt };
          }),
          center,
          geoJson,
          zoomLevel,
          measurementFontSize,
        }),
        totalSqft: layers.reduce((prev, cur) => {
          return prev + parseInt(cur.sqFt);
        }, 0),
      };

      const result = await saveSketchApi(updatedSketch);

      if (result) {
        if ((!sketch || !sketch.formAnswerSketchId) && result.formAnswerSketchId) {
          dispatch(updateSketch({ formAnswerSketchId: result.formAnswerSketchId }));
        }
        dispatch(saveSketchSuccess(result));
        setSaving(false);
      } else {
        // dispatch(saveSketchFailure(result));
        setSaving(false);
      }
    } catch (e) {
      setSaving(false);
      console.log(e);
      console.log('error saving sketch');
    }
  };

  const handleLeafletSaveMap = async () => {
    // call parent save function
    const blobUrl = await onSave(sketch.formAnswerSketchId, base64);

    // if a string is passed back, save it as the new blob url
    if (blobUrl) {
      // save sketch so that the zoom level is saved
      await saveSketch(blobUrl);
    }
  };

  const saveTapeSketch = async () => {
    try {
      const totalLivingSqft = Math.round(tapeFeatures.reduce((sum, feature) => sum + (feature.livingSqft || 0), 0));
      const totalNonLivingSqft = Math.round(
        tapeFeatures.reduce((sum, feature) => {
          const typeCode = getFeatureTypeCode(feature.tapeFeatureTypeId, tapeConfig);
          return typeCode === 'DWL' ? sum : sum + (feature.footprintSqft || 0);
        }, 0),
      );

      // The origin anchors this sketch's local feet frame to a real-world lat/lng so
      // Aerial-traced and Tape-drawn features reconcile. Set once, from the property's
      // geocoded center, then never changed so previously-drawn geometry never shifts.
      const originLat = tapeSketch && tapeSketch.originLat != null ? tapeSketch.originLat : center?.lat ?? null;
      const originLng = tapeSketch && tapeSketch.originLng != null ? tapeSketch.originLng : center?.lng ?? null;

      const payload = {
        tapeSketchId: tapeSketch && tapeSketch.tapeSketchId ? tapeSketch.tapeSketchId : undefined,
        orderFormHeaderId,
        sketchImageUri: tapeSketch && tapeSketch.sketchImageUri ? tapeSketch.sketchImageUri : null,
        originLat,
        originLng,
        bearing: tapeSketch && tapeSketch.bearing != null ? tapeSketch.bearing : 0,
        totalLivingSqft,
        totalNonLivingSqft,
        isComplete: tapeSketch ? tapeSketch.isComplete : false,
        features: tapeFeatures.map((feature, index) => ({
          tapeFeatureTypeId: feature.tapeFeatureTypeId,
          tapeFoundationTypeId: feature.tapeFoundationTypeId,
          tapeGarageKindId: feature.tapeGarageKindId,
          stories: feature.stories,
          storiesAbove: feature.storiesAbove,
          isCathedral: feature.isCathedral,
          sortOrder: index,
          footprintSqft: feature.footprintSqft,
          livingSqft: feature.livingSqft,
          vertices: (feature.vertices || []).map((v, vIndex) => ({ sortOrder: vIndex, x: v.x, y: v.y })),
        })),
      };

      const result = await saveTapeSketchApi(payload);

      if (result) {
        dispatch(saveTapeSketchSuccess(result));
      }
      setTapeSaving(false);
    } catch (e) {
      setTapeSaving(false);
      console.log(e);
      console.log('error saving tape sketch');
    }
  };

  const convertTapeSketchToJpg = async () => {
    if (!tapeSketchSheetRef.current) {
      return console.log('no tape sketch sheet ref available');
    }

    try {
      const blob = await tapeSketchSheetRef.current.renderJpg();
      if (!blob) {
        return;
      }
      const dataUrl = await blobToBase64(blob);
      setTapeBase64(dataUrl);

      const img = document.createElement('img');
      img.src = dataUrl;
      img.style.maxWidth = '100%';
      img.id = TAPE_CANVAS_ID;

      const canvasElement = document.getElementById(TAPE_CANVAS_ID);
      canvasElement.parentNode.replaceChild(img, canvasElement);
    } catch (error) {
      console.log(error);
    }
  };

  const handleTapeSaveMap = async () => {
    if (!tapeSketch || !tapeSketch.tapeSketchId || !tapeBase64) {
      return;
    }

    try {
      const uploadResponse = await uploadTapeSketchApi(tapeSketch.tapeSketchId, tapeBase64);
      const sketchImageUri = await uploadResponse.json();
      await completeTapeSketchApi(tapeSketch.tapeSketchId);
      dispatch(saveTapeSketchSuccess({ ...tapeSketch, sketchImageUri, isComplete: true }));
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  /**
   * When a leaflet layer is created, we save it in state as the last object
   * that was created. This opens the dialog to create and set options for the layer,
   * @param {object} layer - Layer instance
   */
  const handleLeafletLayerCreated = event => {
    setLeafletData(event);
  };

  /**
   * When a layer is added to leaflet programatically.
   * @param {object} layer - Layer instance
   */
  const handleLeafletLayerAdded = event => {
    const leafletLayer = event.sourceTarget;

    layers.forEach(layer => {
      if (!layer.leafletId) {
        dispatch(updateLayer(leafletLayer.options.layerId, 'id', leafletLayer.options.layerId));
      }
    });
  };

  /**
   * When a leaflet layer is updated, update the layer
   * @param {Object} layer - Leaflet Layer instance
   */
  const handleLeafletLayerUpdated = event => {
    if (event && event.layers && event.layers.getLayers() && event.layers.getLayers().length) {
      event.layers.eachLayer(layer => {
        try {
          const newLatLngs = snapLatLngs(
            layer.getLatLngs()[0].map(latLng => ({ lat: latLng.lat, lng: latLng.lng })),
            layers.filter(l => l.id !== layer.options.layerId),
          );
          dispatch(updateLayer(layer.options.layerId, 'latLngs', newLatLngs));
          dispatch(updateLayer(layer.options.layerId, 'lastUpdate', Date.now()));
        } catch (e) {
          console.log('e', e);
        }
      });
    }
  };

  // When a layer is saved through closing the dialog(save and close), we already have all the info
  // we need in state, so just clear the current leaflet drawing for the next shape.
  const handleLeafletLayerSave = layer => {
    setLeafletData(null);
  };

  /**
   * When a layer is canceled, reset the leaflet data state variable, close the dialog, and reset the leaflet map.
   */
  const handleLeafletLayerCancel = () => {
    setLeafletData(null);
    dispatch(deleteLayer(leafletData.layer._leaflet_id));
  };

  const handleLeafletLayerDeleted = event => {
    try {
      const layerIdsToDelete = Object.values(event.layers._layers)
        .map(layer => layer.options.layerId)
        .filter(Boolean);

      dispatch(deleteLayers(layerIdsToDelete));
    } catch (error) {
      console.log({ error });
    }
  };

  const handleLeafletLayerClicked = event => {
    try {
      setShowLayerHasLabelError(false);

      const { layerId } = event.target.options;

      if (layerId === selectedLayerId) {
        dispatch(setSelectedLayerId(null));
      } else if (layerId) {
        dispatch(setSelectedLayerId(layerId));
      }
    } catch (error) {
      console.log('leaflet layer ID does not exist');
    }
  };

  const handleOnZoomEnd = event => {
    const newZoomLevel = event.target._zoom || 20;

    dispatch(updateZoom(newZoomLevel));
  };
  const handleOnMoveEnd = event => {
    if (!mapRef.current) {
      return console.log('mapRef is not active');
    }
    const newMapCenter = mapRef.current.leafletElement.getCenter();

    dispatch(updateCenter(newMapCenter));
  };

  const handleMeasurementFontSizeChange = newFontSize => dispatch(updateMeasurementFontSize(newFontSize));

  const convertWithHtml2Canvas = async () => {
    const { container: mapElement } = mapRef.current;

    // hide all control elements (buttons in the corners of the leaflet container)
    const elementsToHide = Array.from(mapElement.querySelectorAll('.leaflet-control-container'));
    elementsToHide.forEach(el => {
      el.setAttribute('data-html2canvas-ignore', true);
    });

    try {
      // convert to canvas
      // allowTaint allows cross-origin images to be loaded into the canvas
      // useCORS allows the images instead of showing 'tainted canvases cannot be exported' error
      const canvas = await html2canvas(mapElement, { allowTaint: true, useCORS: true });

      const base64Data = canvas.toDataURL();
      setBase64(base64Data);

      canvas.id = CANVAS_ID;

      return canvas;
    } catch (error) {
      console.error('oops, something went wrong!', error);
      throw error;
    }
  };

  const convertMapToCanvas = async () => {
    if (!mapRef.current) {
      return console.log('no map ref available');
    }

    try {
      const canvas = await convertWithHtml2Canvas();

      // set the canvas in the modal
      const canvasElement = document.getElementById(CANVAS_ID);
      canvasElement.parentNode.replaceChild(canvas, canvasElement);
    } catch (error) {
      console.log(error);
    }
  };

  const handleAddLeafletLayerLabel = () => {
    setShowLayerHasLabelError(false);

    const selectedLayer = layers.find(l => l.id === selectedLayerId);

    if (!selectedLayer) {
      console.log('cannot add label... layer not found');
      return;
    }

    if (selectedLayer.labelLatLng) {
      setShowLayerHasLabelError(true);
      return;
    }

    const layerCenter = getLabelStartingPosition(selectedLayer.latLngs);

    dispatch(updateLayer(selectedLayerId, 'labelLatLng', layerCenter));
    dispatch(updateLayer(selectedLayerId, 'labelText', 'New Label'));
  };

  const handleLeafletLayerLabelMoved = (event, layerId) => {
    //
    const newLabelLatLng = event.target._latlng;
    console.log('layer label update', { event, layerId });

    dispatch(updateLayer(layerId, 'labelLatLng', newLabelLatLng));
  };

  if (!center) {
    return null;
  }

  if (loading) {
    return <h1>Loading</h1>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', minHeight: 0 }}>
      <div style={{ display: 'none' }}>
        <TapeSketchSheet ref={tapeSketchSheetRef} features={tapeFeatures} tapeConfig={tapeConfig} />
      </div>
      <Grid container spacing={2} style={{ flex: '1 1 auto', minHeight: 0 }}>
        <Grid item xs={9} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Tabs
            value={mode}
            onChange={(evt, val) => dispatch(setMode(val))}
            indicatorColor="primary"
            textColor="primary"
            style={{ marginBottom: 8, flexShrink: 0 }}
          >
            <Tab value="aerial" label="Aerial" />
            <Tab value="tape" label="Tape" />
          </Tabs>

          {mode === 'aerial' && <ConnectedAerialCanvas center={center} zoomLevel={zoomLevel} />}

          {mode === 'tape' && <ConnectedTapeCanvas />}
        </Grid>
        <Grid item xs={3} style={{ height: '100%' }}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ flex: '1 1 auto', overflow: 'auto', minHeight: 0 }}>
              <TapeFeaturePanel />
            </div>
            <div style={{ flexShrink: 0 }}>
              <SketchPreviewButton
                title="Preview Sketch"
                generatePreview={convertTapeSketchToJpg}
                onSave={handleTapeSaveMap}
                isGenerating={tapeGeneratingCanvas}
              >
                <div className="row-center-center">
                  <div id={TAPE_CANVAS_ID} />
                </div>

                {tapeGeneratingCanvas && (
                  <Typography variant="subtitle1" className={'row-center-center'} style={{ minHeight: 200 }}>
                    {'Generating sketch image... '}
                    <CircularProgress size={50} style={{ marginLeft: 25 }} />
                  </Typography>
                )}
              </SketchPreviewButton>
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
});

SketchTool.propTypes = {
  orderLineId: PropTypes.string,
  images: PropTypes.array,
  policyHolderLat: PropTypes.number,
  policyHolderLng: PropTypes.number,
};

export default SketchTool;
